import React, { useState } from "react";
import { db } from "../firebase/firebaseConfig";
import { collection, addDoc, serverTimestamp, doc, updateDoc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import PhotoGallery from "../components/PhotoGallery";
import { app } from "../firebase/firebaseConfig";

const WorkRequest = () => {
  const [form, setForm] = useState({
    name: "",
    location: "",
    requestType: "",
    description: "",
    priority: "Normal",
    dueDate: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState("");
  const [created, setCreated] = useState(null); // {id, sharepoint:{folderItemId, folderWebUrl, shareUrl}}

  const onChange = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const createFolderFor = async (jobId) => {
    const fns = getFunctions(app, "europe-west2");
    const call = httpsCallable(fns, "spEnsureFolder");
    const { data } = await call({
      type: "workRequest",
      subId: jobId,
    });
    await updateDoc(doc(db, "work_requests", jobId), {
      sharepoint: {
        folderItemId: data.folderItemId,
        folderWebUrl: data.folderWebUrl,
        shareUrl: data.shareUrl || null,
      },
    });
    return data;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMsg("");
    try {
      // 1) Create WR doc
      const docRef = await addDoc(collection(db, "work_requests"), {
        ...form,
        status: "new",
        timestamp: serverTimestamp(),
        engineerNotes: "",
        acknowledgedBy: "",
      });

      // 2) Create SharePoint folder + store IDs
      const sp = await createFolderFor(docRef.id);

      // 3) Read final doc (to show on screen)
      const snap = await getDoc(doc(db, "work_requests", docRef.id));
      setCreated({ id: docRef.id, ...(snap.exists() ? snap.data() : {}), sharepoint: { ...sp } });

      setMsg("✅ Work request submitted.");
      setForm({ name: "", location: "", requestType: "", description: "", priority: "Normal", dueDate: "", notes: "" });
    } catch (err) {
      console.error(err);
      setMsg("❌ " + (err.message || "Error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-6">
      <div className="bg-white shadow-lg rounded-lg p-6 w-full max-w-2xl border border-gray-300">
        <h1 className="text-2xl font-semibold text-center text-gray-800 mb-4">Submit a Work Request</h1>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-700 font-medium mb-1">Your Name</label>
            <input name="name" value={form.name} onChange={onChange} required className="w-full p-2 border rounded-md focus:ring-2 focus:ring-primary focus:outline-none"/>
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-1">Location</label>
            <input name="location" value={form.location} onChange={onChange} required className="w-full p-2 border rounded-md"/>
          </div>

          <div className="md:col-span-2">
            <label className="block text-gray-700 font-medium mb-1">Request Type</label>
            <input name="requestType" value={form.requestType} onChange={onChange} placeholder="Install plug socket" required className="w-full p-2 border rounded-md"/>
          </div>

          <div className="md:col-span-2">
            <label className="block text-gray-700 font-medium mb-1">Description</label>
            <textarea name="description" value={form.description} onChange={onChange} required className="w-full p-2 border rounded-md"/>
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-1">Priority</label>
            <select name="priority" value={form.priority} onChange={onChange} className="w-full p-2 border rounded-md">
              <option>Low</option><option>Normal</option><option>High</option><option>Urgent</option>
            </select>
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-1">Target Date (optional)</label>
            <input type="date" name="dueDate" value={form.dueDate} onChange={onChange} className="w-full p-2 border rounded-md"/>
          </div>

          <div className="md:col-span-2">
            <label className="block text-gray-700 font-medium mb-1">Notes</label>
            <textarea name="notes" value={form.notes} onChange={onChange} className="w-full p-2 border rounded-md"/>
          </div>

          <div className="md:col-span-2">
            <button disabled={submitting} type="submit" className="w-full bg-primary text-white py-2 rounded-md hover:bg-indigo-700 transition text-lg font-semibold">
              {submitting ? "Submitting…" : "Submit"}
            </button>
          </div>
        </form>

        {msg && <p className="text-center mt-4 text-lg font-medium">{msg}</p>}

        {created?.sharepoint?.folderItemId && (
          <div className="mt-6 p-4 border rounded bg-gray-50">
            <p className="font-semibold mb-2">Work Request Created</p>
            <p><strong>ID:</strong> {created.id}</p>
            {created.sharepoint.shareUrl && (
              <p className="mt-2">
                <strong>Upload photos (internal link): </strong>
                <a className="text-indigo-700 underline break-all" href={created.sharepoint.shareUrl} target="_blank" rel="noreferrer">
                  Open upload folder
                </a>
              </p>
            )}
            <p className="mt-4 font-semibold">Photos</p>
            <PhotoGallery folderItemId={created.sharepoint.folderItemId} />
            <DirectUploader folderItemId={created.sharepoint.folderItemId} />
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkRequest;

/** Optional: simple direct uploader using upload sessions (good for < ~4MB per file) */
function DirectUploader({ folderItemId }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");

  const onPick = (e) => setFiles(Array.from(e.target.files || []));

  const upload = async () => {
    if (!files.length || !folderItemId) return;
    setBusy(true); setStatus("");
    try {
      const fns = getFunctions(app, "europe-west2");
      const createSession = httpsCallable(fns, "spCreateUploadSession");
      for (const file of files) {
        const { data } = await createSession({ folderItemId, fileName: file.name });
        const r = await fetch(data.uploadUrl, {
          method: "PUT",
          headers: {
            "Content-Length": file.size,
            "Content-Range": `bytes 0-${file.size - 1}/${file.size}`,
          },
          body: file,
        });
        if (!r.ok) throw new Error(await r.text());
      }
      setStatus("✅ Uploaded. Refresh photos above if needed.");
    } catch (e) {
      console.error(e);
      setStatus("❌ " + (e.message || "Upload failed"));
    } finally {
      setBusy(false);
    }
  };

  if (!folderItemId) return null;
  return (
    <div className="mt-4">
      <input type="file" accept="image/*" multiple onChange={onPick} className="block mb-2"/>
      <button onClick={upload} disabled={busy || !files.length} className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50">
        {busy ? "Uploading…" : "Upload selected"}
      </button>
      {status && <p className="mt-2 text-sm">{status}</p>}
    </div>
  );
}
